import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { ModuleCode, PermissionCode } from '@pms/shared';
import { useAuth } from '@/hooks/useAuth';
import { useModule } from '@/hooks/useModule';
import { usePermission } from '@/hooks/usePermission';

interface ProtectedRouteProps {
  children: ReactNode;
  permission?: PermissionCode;
  module?: ModuleCode;
}

export function ProtectedRoute({ children, permission, module }: ProtectedRouteProps) {
  const { isAuthenticated, loading } = useAuth();
  const perms = usePermission();
  const modules = useModule();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (module && !modules.has(module)) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 px-4 text-center">
        <h1 className="text-3xl font-bold">403</h1>
        <p className="text-muted-foreground">
          This module is not enabled for your account. Ask an admin to grant access.
        </p>
      </div>
    );
  }

  if (permission && !perms.has(permission)) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 px-4 text-center">
        <h1 className="text-3xl font-bold">403</h1>
        <p className="text-muted-foreground">You don't have permission to access this page.</p>
      </div>
    );
  }

  return <>{children}</>;
}
